import React, { useState, useEffect } from 'react';
import RichBodyEditor from '../RichBodyEditor';
import TemplateLibrary from '../TemplateLibrary';

// Trimmed sibling of granularInterconnecting/ComposerPanel.js — same
// mode-toggle/seeding/AI-assist pattern, but no field-picker section (a
// direct email has no suggested-person cards to configure fields for).
const DirectComposerPanel = ({
  composerMode, setComposerMode,
  sharedTemplate, setSharedTemplate,
  perRecipientDrafts, setPerRecipientDrafts,
  emailedMembers, communityId, authedFetch,
}) => {
  const [instructions, setInstructions] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState('');
  const [activeRecipientId, setActiveRecipientId] = useState('');

  useEffect(() => {
    if (emailedMembers.length === 0) {
      setActiveRecipientId('');
      return;
    }
    if (!emailedMembers.some((m) => m.userId === activeRecipientId)) {
      setActiveRecipientId(emailedMembers[0].userId);
    }
  }, [emailedMembers, activeRecipientId]);

  const handleModeChange = (newMode) => {
    if (newMode === 'per-recipient') {
      setPerRecipientDrafts((prev) => {
        const next = { ...prev };
        emailedMembers.forEach((m) => {
          const existing = next[m.userId];
          if (!existing || (!existing.subject && !existing.body)) {
            next[m.userId] = { subject: sharedTemplate.subject, body: sharedTemplate.body };
          }
        });
        return next;
      });
    }
    setComposerMode(newMode);
  };

  const currentDraft = composerMode === 'per-recipient'
    ? (perRecipientDrafts[activeRecipientId] || { subject: '', body: '' })
    : sharedTemplate;

  const updateCurrentDraft = (patch) => {
    if (composerMode === 'per-recipient') {
      if (!activeRecipientId) return;
      setPerRecipientDrafts((prev) => ({
        ...prev,
        [activeRecipientId]: { ...(prev[activeRecipientId] || { subject: '', body: '' }), ...patch },
      }));
    } else {
      setSharedTemplate((prev) => ({ ...prev, ...patch }));
    }
  };

  const handleAiAssist = async () => {
    setAiLoading(true);
    setAiError('');
    try {
      const action = currentDraft.subject || currentDraft.body ? 'tweak' : 'draft';
      const result = await authedFetch('/admin/direct-email/ai-assist', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action,
          communityId,
          instructions,
          currentSubject: currentDraft.subject,
          currentBody: currentDraft.body,
        }),
      });
      updateCurrentDraft({ subject: result.subject, body: result.htmlBody });
    } catch (err) {
      setAiError(err.message);
    } finally {
      setAiLoading(false);
    }
  };

  return (
    <div className="as-email-compose">
      <div className="as-suggestion-mode-toggle">
        <button type="button" className={`as-filter-tab ${composerMode === 'shared' ? 'as-filter-tab--active' : ''}`} onClick={() => handleModeChange('shared')}>
          One shared email
        </button>
        <button type="button" className={`as-filter-tab ${composerMode === 'per-recipient' ? 'as-filter-tab--active' : ''}`} onClick={() => handleModeChange('per-recipient')}>
          Separate email per recipient
        </button>
      </div>

      {composerMode === 'per-recipient' && emailedMembers.length > 0 && (
        <div className="as-suggestion-recipient-tabs">
          {emailedMembers.map((m) => (
            <button
              key={m.userId}
              type="button"
              className={`as-filter-tab ${activeRecipientId === m.userId ? 'as-filter-tab--active' : ''}`}
              onClick={() => setActiveRecipientId(m.userId)}
            >
              {m.firstName || m.userId}
            </button>
          ))}
        </div>
      )}

      <TemplateLibrary
        authedFetch={authedFetch}
        subject={currentDraft.subject}
        body={currentDraft.body}
        onLoad={updateCurrentDraft}
      />

      <div className="as-email-field">
        <label>Subject</label>
        <input type="text" value={currentDraft.subject || ''} onChange={(e) => updateCurrentDraft({ subject: e.target.value })} placeholder="A quick note, {{firstName}}" />
      </div>

      <div className="as-email-field">
        <label>Body — {'{{firstName}}'} and {'{{communityName}}'} are replaced automatically</label>
        <RichBodyEditor
          value={currentDraft.body}
          onChange={(html) => updateCurrentDraft({ body: html })}
          placeholder="Hi {{firstName}}, ..."
        />
      </div>

      <div className="as-suggestion-ai-assist">
        {aiError && <div className="as-error">{aiError}</div>}
        <input
          type="text"
          value={instructions}
          onChange={(e) => setInstructions(e.target.value)}
          placeholder="Instructions for the AI (e.g. warm, ask if they'd help with marketing)"
        />
        <button type="button" className="as-filter-tab" onClick={handleAiAssist} disabled={aiLoading || !communityId}>
          {aiLoading ? 'Thinking…' : '✨ AI Draft / Tweak'}
        </button>
      </div>
    </div>
  );
};

export default DirectComposerPanel;
